import { useEffect, useRef } from 'react'
import { chooseSnapshot, snapshotHealthy, AccountSnapshot, SNAPSHOT_HEALTH_TICK_MS } from '../utils/accountSnapshot'

// 协议见后端 app/services/cfd_ws.py
// 服务器帧: {"type":"snapshot","seq":N,"ts":ms,"data":{token_id,positions,orders,account}}
// 客户端帧: "ping" → 服务器回 {"type":"pong","ts":ms}

const PING_INTERVAL_MS = 30000
const PONG_TIMEOUT_MS = 10000
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000

export function cfdWsUrl(stream: 'cfd' | 'live' = 'cfd'): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/api/v1/ws/${stream}`
}

interface UseCfdLiveSocketOptions {
  enabled?: boolean
  accountId?: number
  stream?: 'cfd' | 'live'
  onSnapshot?: (data: unknown) => void
  onAccountEvents?: (accounts: Array<{token_id: number; cursor: number}>) => void
  onStatusChange?: (connected: boolean) => void
}

/**
 * CFD 看板实时推送连接（指数退避重连 + ping/pong 保活）。
 * 断开期间由调用方自行降级轮询（onStatusChange(false)）。
 */
export function useCfdLiveSocket({
  enabled = true,
  accountId,
  stream = 'cfd',
  onSnapshot,
  onAccountEvents,
  onStatusChange,
}: UseCfdLiveSocketOptions = {}) {
  const cbRef = useRef({ onSnapshot, onAccountEvents, onStatusChange })
  useEffect(() => {
    cbRef.current = { onSnapshot, onAccountEvents, onStatusChange }
  })

  useEffect(() => {
    if (!enabled) {
      cbRef.current.onStatusChange?.(false)
      return
    }

    let ws: WebSocket | null = null
    let disposed = false
    let retry = 0
    let reconnectTimer: number | undefined
    let pingTimer: number | undefined
    let pongTimer: number | undefined
    let healthTimer: number | undefined
    let latest: AccountSnapshot | undefined

    const clearTimers = () => {
      window.clearTimeout(reconnectTimer)
      window.clearInterval(pingTimer)
      window.clearTimeout(pongTimer)
      window.clearInterval(healthTimer)
      reconnectTimer = pingTimer = pongTimer = undefined
    }

    const reportHealth = () => cbRef.current.onStatusChange?.(ws?.readyState === WebSocket.OPEN && snapshotHealthy(latest))
    document.addEventListener('visibilitychange', reportHealth)
    window.addEventListener('focus', reportHealth)

    const connect = () => {
      clearTimers()
      try {
        ws = new WebSocket(cfdWsUrl(stream))
      } catch {
        cbRef.current.onStatusChange?.(false)
        return
      }

      ws.onopen = () => {
        retry = 0
        cbRef.current.onStatusChange?.(false)
        healthTimer = window.setInterval(reportHealth, SNAPSHOT_HEALTH_TICK_MS)
        pingTimer = window.setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send('ping')
            pongTimer = window.setTimeout(() => {
              try {
                ws?.close()
              } catch {
                /* noop */
              }
            }, PONG_TIMEOUT_MS)
          }
        }, PING_INTERVAL_MS)
      }

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data))
          if (msg?.type === 'pong') {
            window.clearTimeout(pongTimer)
            pongTimer = undefined
            return
          }
          if (msg?.type === 'account_events' && Array.isArray(msg?.data?.accounts)) {
            cbRef.current.onAccountEvents?.(msg.data.accounts)
            return
          }
          if (msg?.type === 'snapshot' && msg?.data) {
            const incoming = accountId == null ? msg.data : (msg.data.tokens || [msg.data]).find(
              (frame: AccountSnapshot) => String(frame.account_id ?? frame.token_id) === String(accountId))
            if (!incoming) return
            latest = chooseSnapshot(latest, incoming)
            cbRef.current.onSnapshot?.(latest)
            reportHealth()
          }
        } catch {
          /* 忽略非法帧 */
        }
      }

      ws.onclose = () => {
        if (disposed) return
        cbRef.current.onStatusChange?.(false)
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** retry, RECONNECT_MAX_MS)
        retry += 1
        reconnectTimer = window.setTimeout(connect, delay)
      }

      ws.onerror = () => {
        try {
          ws?.close()
        } catch {
          /* noop */
        }
      }
    }

    connect()
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', reportHealth)
      window.removeEventListener('focus', reportHealth)
      clearTimers()
      try {
        ws?.close()
      } catch {
        /* noop */
      }
      ws = null
    }
  }, [enabled, accountId, stream])
}
